import debounce from 'lodash/debounce';
import React, { createContext, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useParams } from 'react-router';

import { PayorType } from '@customTypes/constants';
import { fetchCampaignLandingBySlug } from '@store/actions';
import { isMinorDOB } from '@utils/dates';

import { getPartnerConfigByUrlPath, PartnerConfig, transformApiToPartnerConfig } from './pages/Launch/partnerConfig';
import { OrderActions, type OrderActionType } from './types/Order.actions';
import type { OrderContextValue, OrderState, SubjectPayload } from './types/Order.types';
import { clearWizardState, loadOrderState, saveOrderState } from './utils/orderStorage';
import { canSelectProduct, getAllDeselectedProductIds, getDefaultSelectedProductIds } from './utils/productSelection';

// Reducer
const reducer = (state: OrderState, action: OrderActionType): OrderState => {
  switch (action.type) {
    case OrderActions.SET_CAMPAIGN_CODE:
      return { ...state, campaign_code: action.value };

    case OrderActions.SET_EXTERNAL_MEMBER_ID:
      return { ...state, external_member_id: action.value };

    case OrderActions.SET_SUBJECT:
      return { ...state, subject: action.value };

    case OrderActions.SET_GUARDIAN:
      return { ...state, guardian: action.value };

    case OrderActions.MERGE_SUBJECT: {
      const next: SubjectPayload = {
        ...state.subject,
        ...(action.value || {}),
        name: { ...state.subject.name, ...(action.value?.name || {}) },
        demographics: { ...state.subject.demographics, ...(action.value?.demographics || {}) },
        contact_info: action.value?.contact_info ? action.value.contact_info : state.subject.contact_info,
      };
      return { ...state, subject: next };
    }

    case OrderActions.SET_FAMILY_HISTORY:
      return { ...state, family_history: action.value };

    case OrderActions.MERGE_FAMILY_HISTORY: {
      const updates = action.value || {};
      const next: typeof state.family_history = { ...state.family_history };
      (Object.keys(updates) as string[]).forEach(k => {
        const v = (updates as Record<string, (typeof next)[string]>)[k];
        if (v) {
          (next as Record<string, typeof v>)[k] = v;
        }
      });
      return { ...state, family_history: next };
    }

    case OrderActions.SET_PRODUCT_INFORMATION:
      return { ...state, product_information: action.value };

    case OrderActions.MERGE_PRODUCT_INFORMATION:
      return {
        ...state,
        product_information: {
          ...(state.product_information || {}),
          ...(action.value || {}),
        },
      };

    case OrderActions.SET_CONSENTS_GRANTED:
      return { ...state, consents_granted: [...(action.value || [])] };

    case OrderActions.ADD_CONSENT:
      return state.consents_granted.includes(action.value)
        ? state
        : { ...state, consents_granted: [...state.consents_granted, action.value] };

    case OrderActions.PRODUCT_SELECTED: {
      const selectedId = action.value;
      const currentSelectedIds = state.selected_product_ids;

      if (currentSelectedIds.includes(selectedId)) {
        return state;
      }

      const selectedProduct = state.products.find(p => p.product_id === action.value);

      if (selectedProduct && canSelectProduct(selectedProduct, currentSelectedIds)) {
        return {
          ...state,
          selected_product_ids: [...currentSelectedIds, action.value],
        };
      }

      return state;
    }

    case OrderActions.PRODUCT_DESELECTED: {
      const deselectedId = action.value;
      const deselectedProduct = state.products.find(p => p.product_id === deselectedId);
      const currentSelectedIds = state.selected_product_ids;

      if (!deselectedProduct || currentSelectedIds.length <= 1) {
        return state;
      }

      const deselectIds = getAllDeselectedProductIds(state.products, currentSelectedIds, deselectedId);
      const nextSelectedIds = currentSelectedIds.filter(id => !deselectIds.includes(id));

      // Don't allow this deselect if the cascade would leave zero products selected.
      if (nextSelectedIds.length > 0) {
        return {
          ...state,
          selected_product_ids: nextSelectedIds,
        };
      }

      return state;
    }

    case OrderActions.ELIGIBILITY_UPDATED: {
      const { campaign: nextCampaign, pricing: nextPricing, products: nextProducts } = action.value;

      return {
        ...state,
        campaign_code: nextCampaign?.campaign_code || undefined,
        eligibilityChecked: true,
        external_member_id: nextCampaign?.member?.id || undefined,
        pricing: nextPricing,
        products: nextProducts,
        selected_product_ids: getDefaultSelectedProductIds(nextProducts || []),
      };
    }

    case OrderActions.ELIGIBILITY_RESET: {
      return {
        ...state,
        eligibilityChecked: false,
        guardian: null,
        is_rerequisition: false,
        pricing: undefined,
        products: [],
        selected_product_ids: [],
      };
    }

    case OrderActions.PRICING_FETCHED:
      return { ...state, pricing: action.value };

    case OrderActions.PAYMENT_INTENT_CREATED:
    case OrderActions.PAYMENT_INTENT_UPDATED:
      return { ...state, paymentIntent: action.value };

    case OrderActions.RESET:
      return initialEmptyState();

    case OrderActions.SET_IS_REREQUISITION:
      return { ...state, is_rerequisition: action.value };

    default:
      return state;
  }
};

export const OrderContext = createContext<OrderContextValue | undefined>(undefined);

function initialEmptyState(): OrderState {
  return {
    campaign_code: undefined,
    eligibilityChecked: false,
    external_member_id: undefined,
    subject: { name: {}, demographics: {}, contact_info: [{}] },
    family_history: {},
    guardian: null,
    product_information: undefined,
    consents_granted: [],
    paymentIntent: undefined,
    pricing: undefined,
    products: [],
    selected_product_ids: [],
    is_rerequisition: false,
  };
}

// Drafts saved before product selection existed (and injected test state) carry no
// selection; everything eligible is selected by default, except products that depend
// on another product (e.g. APOE, which requires Single-Gene Risk), which must be
// opted into even if that requirement was already met by a prior order.
function withDefaultProductSelection(state: OrderState): OrderState {
  if (state.selected_product_ids?.length) return state;
  return { ...state, selected_product_ids: getDefaultSelectedProductIds(state.products ?? []) };
}

// Provider
export const OrderProvider: React.FC<React.PropsWithChildren<{ injectedInitialState?: OrderState }>> = ({
  injectedInitialState,
  children,
}) => {
  // Extract partner URL path from route params
  const { partner } = useParams<{ partner?: string }>();

  const [partnerConfig, setPartnerConfig] = useState<PartnerConfig | null>(null);
  const [hasInvalidPartner, setHasInvalidPartner] = useState<boolean>(false);
  const [isLoadingPartner, setIsLoadingPartner] = useState<boolean>(false);

  useEffect(() => {
    if (!partner) {
      setPartnerConfig(null);
      setHasInvalidPartner(false);
      setIsLoadingPartner(false);
      return;
    }

    const localConfig = getPartnerConfigByUrlPath(partner);
    if (localConfig) {
      setPartnerConfig(localConfig);
      setHasInvalidPartner(false);
      setIsLoadingPartner(false);
      return;
    }

    // If not found locally, fetch from API
    let isMounted = true;
    setIsLoadingPartner(true);
    const fetchApiConfig = async () => {
      try {
        const response = await fetchCampaignLandingBySlug(partner);
        if (!isMounted) return;

        const dynamicConfig = transformApiToPartnerConfig(response, partner);
        setPartnerConfig(dynamicConfig);
        setHasInvalidPartner(false);
      } catch (err) {
        if (!isMounted) return;
        console.error('Failed to fetch partner config from API', err);
        setPartnerConfig(null);
        setHasInvalidPartner(true);
      } finally {
        if (isMounted) setIsLoadingPartner(false);
      }
    };

    fetchApiConfig();

    return () => {
      isMounted = false;
    };
  }, [partner]);

  const initialState: OrderState = useMemo(() => {
    const sessionState = loadOrderState();
    const base: OrderState = withDefaultProductSelection(injectedInitialState || sessionState || initialEmptyState());

    // For partnership routes, set campaign_code from partner config
    if (partner && partnerConfig?.campaignCode) {
      return { ...base, campaign_code: partnerConfig.campaignCode };
    }

    // For order routes, don't use campaign_code from sessionStorage initially
    if (!partner) {
      return { ...base, campaign_code: undefined };
    }

    return base;
  }, [injectedInitialState, partner, partnerConfig]);

  const [state, dispatch] = useReducer(reducer, initialState);

  // Debounce state persistence to reduce JSON.stringify calls during rapid typing
  const debouncedSave = useMemo(
    () =>
      debounce((state: OrderState) => {
        saveOrderState(state);
      }, 300),
    [],
  );

  // Persist state changes for all partner order routes (both /order/* and /partnership/*)
  useEffect(() => {
    debouncedSave(state);
    return () => {
      debouncedSave.flush();
    };
  }, [state, debouncedSave]);

  const resetEligibility = useCallback(() => {
    clearWizardState();
    dispatch({ type: OrderActions.ELIGIBILITY_RESET });
  }, []);

  const deselectProduct = useCallback((productId: string) => {
    dispatch({ type: OrderActions.PRODUCT_DESELECTED, value: productId });
  }, []);

  const selectProduct = useCallback((productId: string) => {
    dispatch({ type: OrderActions.PRODUCT_SELECTED, value: productId });
  }, []);

  const value = useMemo<OrderContextValue>(() => {
    let requiresPaymentStep = true;
    if (state.pricing) {
      const { payor_type: payorType, price_cents: priceCents } = state.pricing;
      requiresPaymentStep = priceCents > 0 && payorType === PayorType.SELF;
    }
    const selectedProducts = (state.products ?? []).filter(p => state.selected_product_ids.includes(p.product_id));
    const requiresQuestionnaireStep = selectedProducts.some(p => p.requires_family_history === true);
    const isMinorOrder = !!partnerConfig?.allowsMinorWithGuardian && isMinorDOB(state.subject?.demographics?.dob || '');
    return {
      state,
      dispatch,
      partnerConfig,
      partner,
      campaign_code: partnerConfig?.campaignCode ?? state.campaign_code,
      isPartnerValid: !!partnerConfig,
      hasInvalidPartner,
      isLoadingPartner,
      isMinorOrder,
      resetEligibility,
      deselectProduct,
      selectProduct,
      requiresPaymentStep,
      requiresQuestionnaireStep,
      selectedProducts,
    };
  }, [
    state,
    dispatch,
    partnerConfig,
    partner,
    hasInvalidPartner,
    isLoadingPartner,
    resetEligibility,
    deselectProduct,
    selectProduct,
  ]);

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};
